import type { ComputerUseRecordingState, ComputerUseRunState } from "../reducers/chatReducer";

/**
 * Where each browser *session* belongs in the transcript.
 *
 * The unit here is the session, not the run, and that is the whole point. A run is one tool
 * call, but it can open several browsers: an attempt that stops at a sign-in wall, the sign-in
 * itself, the retry that follows. Between them sit the technician's own decisions and Theo's
 * replies about them.
 *
 * Placing by run put all of a run's recordings in one row, at the point the *first* browser
 * opened -- so the sign-in card and the "they've signed in, retrying" message that belong
 * between them were somewhere else entirely, and the row read as a set of unlabelled parts.
 * Placing by session puts each where it happened, and the surrounding messages explain it.
 *
 * The times themselves come from the sequence of writes rather than from clock precision, so
 * this survives a little skew between the two databases. Both pools read timestamps as UTC.
 */

/** One browser, and what there is to show of it: a finished recording, or the live view. */
export type RunSession = {
  run: ComputerUseRunState;
  /** The video, once there is one. Null means this is the browser that is still open. */
  recording: ComputerUseRecordingState | null;
  key: string;
  at: number;
};

export type RunPlacement = Map<number, RunSession[]>;

/** Index used for sessions that precede every message. Renders above the first bubble. */
export const BEFORE_FIRST_MESSAGE = -1;

function messageTime(message: any): number {
  const seconds = Number(message?.created_at?.seconds);
  if (Number.isFinite(seconds) && seconds > 0) {
    return seconds * 1000 + Number(message?.created_at?.nanos || 0) / 1e6;
  }
  return Number.NaN;
}

function at(value: string | null | undefined): number {
  if (!value) return Number.NaN;
  return new Date(value).getTime();
}

const LIVE_STATUSES = ["in_queue", "executing", "waiting_on_customer"];

/**
 * Whether this run still has something to draw that is not a finished recording.
 *
 * Three cases, and the last is the one easily missed. A run that is going. A run waiting for
 * its video, which holds the place so the recording appears where the browser was rather than
 * jumping somewhere else when it arrives. And a run the technician *stopped* whose video has
 * not landed yet -- stopping is what closes the session, and closing is what starts the video
 * being made, so a stop lands here almost every time. Dropping that case would leave a
 * cancelled run showing nothing at all until its recording arrived.
 */
function hasOpenPanel(run: ComputerUseRunState): boolean {
  if (run.browserUrl || run.status === "preparing_recording" || LIVE_STATUSES.includes(run.status)) return true;
  return run.status === "cancelled" && run.sessionId !== null && (run.recordings || []).length === 0;
}

/**
 * Every session worth drawing, across every run, each with the time it began.
 *
 * A recording whose session start is unknown falls back to the run's, which is exactly right
 * for the common case of a run with one session and no better answer available.
 */
export function runSessions(runs: ComputerUseRunState[]): RunSession[] {
  const sessions: RunSession[] = [];

  for (const run of runs || []) {
    // A read draws nothing. It changes nothing in the tenant, so there is no recording coming
    // and no live view worth a technician's attention -- the line naming the action being run
    // already says what is happening.
    //
    // Nor does a silent run, for a related reason: these are the repeated looks Theo takes at a
    // job it is waiting on, and a transfer running for hours produces dozens of them. A panel
    // each would bury the conversation under browsers nobody asked for.
    //
    // Except while either is parked at a sign-in. Then the browser on screen is the
    // technician's to type into, and hiding it would ask them to sign in to something they
    // cannot see. The panel goes away again by itself when the run resumes and the status
    // leaves this one.
    if ((run.readOnly || run.silent) && run.status !== "waiting_on_customer") continue;

    for (const recording of run.recordings || []) {
      sessions.push({
        run,
        recording,
        key: `${run.callId}:${recording.session_id || recording.object_name}`,
        at: at(recording.started_at) || at(run.startedAt),
      });
    }

    if (hasOpenPanel(run)) {
      // The live browser is placed by its own session's start, not the job's -- a rescued run's
      // second browser opens long after the job did. Placing both the same way is what lets a
      // running browser be replaced in place by its own recording instead of moving.
      const startedAt = run.sessionId ? run.sessionStartedAt?.[run.sessionId] : null;
      sessions.push({
        run,
        recording: null,
        key: `${run.callId}:live`,
        at: at(startedAt) || at(run.startedAt),
      });
    }
  }

  return sessions;
}

/**
 * Group sessions by the index of the message they render after.
 *
 * A session with no usable start time goes last: it is almost certainly the one running right
 * now, which is where a reader looks for it anyway.
 */
export function placeRuns(messages: any[], runs: ComputerUseRunState[]): RunPlacement {
  const placement: RunPlacement = new Map();
  const sessions = runSessions(runs);
  if (!sessions.length) return placement;

  // Only messages with a readable timestamp can bound anything; a pending or streaming one
  // has no created_at yet and must not swallow every session that follows it.
  const stamps = messages.map(messageTime);

  const ordered = [...sessions].sort((left, right) => {
    if (Number.isNaN(left.at)) return 1;
    if (Number.isNaN(right.at)) return -1;
    return left.at - right.at;
  });

  // Whether ANY message can bound a session at all. A message that is still streaming has no
  // created_at yet, and during a live turn that can be true of every message on screen -- at
  // which point "no message is older than this session" is not evidence that the session came
  // first, it is evidence that nothing is readable.
  const anyStamped = stamps.some((stamp) => !Number.isNaN(stamp));

  for (const session of ordered) {
    // Unplaceable sessions go last, not first. A session we cannot position is far more likely
    // to be the one happening right now than one that predates the whole thread, and guessing
    // the other way is what put a browser opening *now* above the card that asked for it --
    // where it sat until real timestamps arrived and moved it down under the reader.
    const unplaceable = messages.length - 1;

    let index = Number.isNaN(session.at) || !anyStamped ? unplaceable : BEFORE_FIRST_MESSAGE;

    if (!Number.isNaN(session.at) && anyStamped) {
      for (let i = 0; i < stamps.length; i += 1) {
        if (!Number.isNaN(stamps[i]) && stamps[i] <= session.at) index = i;
      }
    }

    placement.set(index, [...(placement.get(index) || []), session]);
  }

  return placement;
}
